import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ResultadoOperacaoLote,
  abrirInscricoesCategoriasEmLote,
} from '../../../../services/categoriaTorneioService';

// Estrutura mínima consumida por este componente. Aceita tanto `CategoriaTorneio` de
// `services/categoriaTorneioService.ts` (usado pelos testes deste componente) quanto o de
// `services/torneioService.ts` (usado por `TournamentInfoTab.tsx`) — os dois tipos, hoje
// distintos no repositório, compartilham `id`/`nome`, então a tipagem estrutural do
// TypeScript aceita ambos sem exigir importar um em detrimento do outro.
interface CategoriaSelecionavel {
  id: string;
  nome: string;
}

interface AbrirInscricoesEmLoteBarProps {
  categorias: CategoriaSelecionavel[];
  selectedIds: Set<string>;
  onClearSelection: () => void;
  onRefresh?: () => void;
}

/**
 * Barra de ação em massa: abrir inscrições de várias categorias de uma vez.
 * Declara o escopo (quantas e quais categorias) ANTES de qualquer execução — na barra e de
 * novo no diálogo de confirmação — e mantém um painel de resultados PERSISTENTE (nunca um
 * Snackbar com timer de auto-fechamento) para que falhas parciais nunca desapareçam sozinhas.
 */
const AbrirInscricoesEmLoteBar: React.FC<AbrirInscricoesEmLoteBarProps> = ({
  categorias,
  selectedIds,
  onClearSelection,
  onRefresh,
}) => {
  const [dialogAberto, setDialogAberto] = useState(false);
  const [executando, setExecutando] = useState(false);
  const [resultados, setResultados] = useState<ResultadoOperacaoLote[] | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  if (selectedIds.size === 0) {
    return null;
  }

  const selecionadas = categorias.filter((c) => selectedIds.has(c.id));

  const handleConfirmar = async () => {
    setExecutando(true);
    try {
      const resultado = await abrirInscricoesCategoriasEmLote(Array.from(selectedIds));
      setResultados(resultado);
      setDialogAberto(false);
      onRefresh?.();
    } catch (e) {
      setErroGeral((e as Error).message);
      setDialogAberto(false);
    } finally {
      setExecutando(false);
    }
  };

  const handleFechar = () => {
    setResultados(null);
    onClearSelection();
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
        <span>
          {selecionadas.length} selecionada(s): {selecionadas.map((c) => c.nome).join(', ')}
        </span>
        <Button
          variant="contained"
          size="small"
          onClick={() => setDialogAberto(true)}
        >
          Abrir inscrições selecionadas
        </Button>
      </div>

      {erroGeral && (
        <div className="alert alert-danger" role="alert">
          {erroGeral}
        </div>
      )}

      {dialogAberto && (
        <Dialog open onClose={() => setDialogAberto(false)}>
          <DialogTitle>Abrir inscrições em lote</DialogTitle>
          <DialogContent>
            <p>Esta ação abrirá as inscrições de {selecionadas.length} categorias:</p>
            <List dense>
              {selecionadas.map((c) => (
                <ListItem key={c.id}>
                  <ListItemText primary={c.nome} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogAberto(false)} disabled={executando}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmar}
              variant="contained"
              color="success"
              disabled={executando}
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {resultados !== null && (
        <div className="border-bottom px-3 py-2">
          <div className="d-flex align-items-center justify-content-between">
            <strong>Resultado da ação em massa</strong>
            <Button size="small" onClick={handleFechar}>
              Fechar
            </Button>
          </div>
          <ul className="mb-0">
            {resultados.map((r) => {
              const nome = categorias.find((c) => c.id === r.id)?.nome ?? r.id;
              return (
                <li key={r.id}>
                  {r.sucesso ? (
                    `✓ ${nome}: inscrições abertas com sucesso`
                  ) : (
                    <>
                      ✗ {nome}: falhou — <span>{r.motivo ?? 'Erro desconhecido'}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

export default AbrirInscricoesEmLoteBar;
